import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type { PageResult } from '../types/result';

const BASE = '/push';

/** 推送厂商：个推主干 + 厂商通道（iOS 直连 APNs）。 */
export type PushVendor = 'getui' | 'huawei' | 'xiaomi' | 'oppo' | 'vivo' | 'honor' | 'ios';

export type PushPlatform = 'android' | 'ios';

/** 投递状态：SENT 已提交 / FAILED 失败 / SKIPPED 跳过（标识无效）。 */
export type PushDeliveryStatus = 'SENT' | 'FAILED' | 'SKIPPED';

/**
 * 设备推送标识登记。端侧登录/启动后调用，登出时调用 `unregisterToken`。
 * token 必须是端侧推送 SDK 返回的真实标识（个推 cid / 厂商 regId / APNs deviceToken），
 * 本地兜底值（如 `device_xxx`）会被后端 42200 拒绝。
 */
export interface PushTokenRegisterRequest {
  deviceId: string;
  vendor: PushVendor;
  token: string;
  platform?: PushPlatform;
  /** 应用编码，缺省 app-pda。 */
  appCode?: string;
  appVersion?: string;
}

export interface PushTokenUnregisterRequest {
  /** 不传则注销当前账号全部设备。 */
  deviceId?: string;
  vendor?: PushVendor;
}

export interface PushTokenRow {
  id: Id;
  userId: Id;
  deviceId: string;
  vendor: string;
  platform: string;
  /** token 掩码（后端不返回明文标识）。 */
  tokenMask: string;
  appCode: string;
  appVersion: string | null;
  status: 'ACTIVE' | 'REVOKED' | string;
  lastActiveAt: string | null;
  createAt: string;
}

/**
 * 推送发送入参。定向优先级：userIds → deviceIds → 当前登录用户。
 * 给他人推送需 `sys:notify:send` 权限，且目标用户须与调用方同租户。
 */
export interface PushSendRequest {
  userIds?: Id[];
  deviceIds?: string[];
  title: string;
  content?: string;
  /** 点击/前台回调的业务负载（JSON 字符串，≤4000 字符）。 */
  payload?: string;
  bizType?: string;
  bizId?: Id;
  /** 强制指定通道（排障用）。 */
  channel?: PushVendor;
}

export interface PushSendItem {
  userId: Id;
  deviceId: string;
  channel: string;
  status: PushDeliveryStatus;
  taskId: string | null;
  error: string | null;
}

export interface PushSendResult {
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  channels: string[];
  items: PushSendItem[];
  message: string;
}

/** 通道自检：configured=false 时 missing 列出缺失的 appsettings 配置键。 */
export interface PushChannelState {
  channel: string;
  configured: boolean;
  missing: string[];
}

export interface PushLogQuery {
  lastId?: Id | null;
  size?: number;
  userId?: Id;
  status?: PushDeliveryStatus;
  channel?: string;
  bizType?: string;
}

export interface PushLogRow {
  id: Id;
  userId: Id;
  bizType: string;
  bizId: Id;
  channel: string;
  vendor: string;
  deviceId: string;
  tokenMask: string;
  title: string;
  status: PushDeliveryStatus;
  taskId: string | null;
  errorMsg: string | null;
  costMs: number;
  createAt: string;
}

/**
 * App 消息推送（个推 + 厂商通道）。
 *
 * 通道选择由服务端按设备登记的 vendor/platform 决定：厂商通道未配置或投递失败时回退个推；
 * 厂商 regId 与个推 cid 不可混用，故端侧必须如实上报 vendor。
 * 凭据未补齐时 `/push/channels` 会返回 configured=false 与缺失键，`/push/send` 返回 42200（不静默丢弃）。
 */
export function createPushApi(http: HttpClient) {
  return {
    /** 登记/更新当前账号的设备推送标识。 */
    registerToken(body: PushTokenRegisterRequest): Promise<PushTokenRow> {
      return http.request<PushTokenRow>({ path: `${BASE}/token`, method: 'POST', body });
    },

    /** 注销设备推送标识（登出/卸载）。返回实际失效的记录数。 */
    unregisterToken(body: PushTokenUnregisterRequest = {}): Promise<{ removed: number }> {
      return http.request<{ removed: number }>({ path: `${BASE}/token/unregister`, method: 'POST', body });
    },

    /** 当前账号已登记设备（token 只回掩码）。 */
    myTokens(): Promise<PushTokenRow[]> {
      return http.request<PushTokenRow[]>({ path: `${BASE}/token/mine` });
    },

    /** 定向投递；逐设备返回 SENT/FAILED/SKIPPED 与厂商原始失败原因。 */
    send(body: PushSendRequest): Promise<PushSendResult> {
      return http.request<PushSendResult>({ path: `${BASE}/send`, method: 'POST', body });
    },

    /** 通道自检（凭据是否补齐），需 sys:notify:send 或 sys:audit:view。 */
    channels(): Promise<PushChannelState[]> {
      return http.request<PushChannelState[]>({ path: `${BASE}/channels` });
    },

    /** 投递留痕（游标分页），需 sys:notify:send 或 sys:audit:view。 */
    logs(query: PushLogQuery = {}): Promise<PageResult<PushLogRow>> {
      return http.request<PageResult<PushLogRow>>({ path: `${BASE}/log`, query: toQuery(query) });
    }
  };
}

export type PushApi = ReturnType<typeof createPushApi>;
